<template>
  <div class="testimonial-feature-container" :class="spaceLevel">
    <!-- Animated background elements -->
    <div class="background-decoration">
      <div class="quote-mark quote-mark-1">"“"</div>
      <div class="quote-mark quote-mark-2">"”"</div>
      <div class="trust-badge">
        <i class="fas fa-shield-alt"></i>
      </div>
    </div>
    
    <div class="testimonial-content">
      <div class="testimonial-inner">
        <!-- Verified badge -->
        <div class="verified-container">
          <div class="verified-badge">
            <i class="fas fa-check-circle"></i>
            <span>Verified Review</span>
          </div>
        </div>
        
        <div class="testimonial-header">
          <div class="patient-avatar">
            <img v-if="ad.patientPhoto" :src="ad.patientPhoto" :alt="ad.patientName" />
            <div v-else class="avatar-placeholder">
              {{ getInitials(ad.patientName) }}
            </div>
            <div class="avatar-glow"></div>
          </div>
          <div class="patient-info">
            <h6 class="patient-name">{{ ad.patientName }}</h6>
            <div class="rating">
              <div class="stars-container">
                <i
                  v-for="star in 5"
                  :key="star"
                  class="star"
                  :class="{ 'filled': star <= ad.rating }"
                  :style="{ animationDelay: (star * 0.1) + 's' }"
                >
                  ★
                </i>
              </div>
              <span class="rating-text">{{ ad.rating }}/5</span>
            </div>
          </div>
        </div>
        
        <div class="testimonial-text">
          <div class="quote-icon">
            <i class="fas fa-quote-left"></i>
          </div>
          <p class="testimonial-quote">{{ ad.testimonial }}</p>
        </div>
        
        <!-- Trust indicators -->
        <div class="trust-indicators">
          <div class="trust-item">
            <i class="fas fa-user-check"></i>
            <span>Verified Patient</span>
          </div>
          <div class="trust-item">
            <i class="fas fa-calendar-check"></i>
            <span>Recent Visit</span>
          </div>
        </div>
      </div>
      
      <!-- Glassmorphism overlay -->
      <div class="glass-overlay"></div>
    </div>
  </div>
</template>

<script>
import { openAdLinkInNewTab } from "@/utils/adLink";

export default {
  name: "TestimonialAd",
  props: {
    ad: {
      type: Object,
      required: true
    },
    spaceLevel: {
      type: String,
      default: 'normal'
    }
  },
  data() {
    return {
      index: 0
    };
  },
  mounted() {
    // Emit loaded event for sizing
    this.$emit('testimonialLoaded', {
      aspectRatio: 1.0, // Square aspect ratio for better layout
      type: 'square'
    });
  },
  methods: {
    openInNewTab(url) {
      openAdLinkInNewTab(url, "noreferrer");
    },
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        year: 'numeric' 
      });
    }
  }
};
</script>

<style scoped>
.testimonial-feature-container {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.testimonial-carousel {
  height: 100%;
  width: 100%;
}

.testimonial-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(18px, 3vw, 28px);
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #2980b9 100%);
  background-size: 200% 200%;
  animation: gradientShift 12s ease-in-out infinite;
  color: white;
  cursor: pointer;
  box-shadow: 
    0 12px 40px rgba(74, 144, 226, 0.4),
    0 6px 20px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  margin: 0 auto;
  container-type: inline-size;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.testimonial-inner {
  width: 100%;
  position: relative;
  z-index: 2;
}

.testimonial-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.patient-avatar {
  width: clamp(48px, 5vw, 64px);
  height: clamp(48px, 5vw, 64px);
  border-radius: 50%;
  overflow: hidden;
  margin-right: clamp(12px, 2vw, 16px);
  flex-shrink: 0;
  position: relative;
  border: 3px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  animation: slideInFromLeft 0.8s ease-out 0.1s both;
}

.patient-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: clamp(16px, 2vw, 22px);
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
}

.patient-info {
  flex-grow: 1;
}

.patient-name {
  font-size: clamp(16px, 2.5vw, 22px);
  font-weight: 700;
  margin: 0 0 clamp(6px, 1vw, 10px) 0;
  color: white;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.01em;
  animation: slideInFromTop 0.8s ease-out 0.3s both;
}

.rating {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.stars-container {
  display: flex;
  gap: 3px;
  margin-bottom: 4px;
}

.star {
  font-size: clamp(14px, 1.8vw, 18px);
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.4s ease;
  animation: starGlow 3s ease-in-out infinite;
}

.star.filled {
  color: #ffd700;
  text-shadow: 0 0 8px rgba(255, 215, 0, 0.6), 0 0 4px rgba(255, 215, 0, 0.4);
  transform: scale(1.1);
}

.rating-text {
  font-size: clamp(11px, 1.4vw, 14px);
  font-weight: 600;
  color: #ffd700;
  margin-left: 8px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.testimonial-text {
  margin-bottom: 15px;
}

.testimonial-quote {
  font-size: clamp(14px, 2.1vw, 18px);
  line-height: 1.6;
  margin: 0;
  font-style: italic;
  opacity: 0.95;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  padding-left: 20px;
  animation: slideInFromBottom 0.8s ease-out 0.5s both;
}

.testimonial-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  opacity: 0.8;
}

.treatment-type {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.date {
  font-weight: 400;
}

/* Enhanced hover effects */
.testimonial-content:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 
    0 20px 55px rgba(74, 144, 226, 0.5),
    0 10px 25px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.testimonial-content:hover .star.filled {
  animation-duration: 1.5s;
}

.testimonial-content:hover .trust-badge {
  animation-duration: 4s;
}

.testimonial-content:hover .avatar-glow {
  opacity: 0.6;
}

/* Carousel controls */
.testimonial-carousel ::v-deep .carousel-inner {
  background-color: transparent !important;
  height: 100%;
}

.testimonial-carousel ::v-deep .carousel-item {
  height: 100%;
}

.testimonial-carousel ::v-deep .carousel-control-prev,
.testimonial-carousel ::v-deep .carousel-control-next {
  background: rgba(255, 255, 255, 0.2);
  width: 40px;
  border-radius: 4px;
}

.testimonial-carousel ::v-deep .carousel-indicators {
  bottom: 10px;
}

.testimonial-carousel ::v-deep .carousel-indicators li {
  background-color: rgba(255, 255, 255, 0.5);
}

.testimonial-carousel ::v-deep .carousel-indicators .active {
  background-color: rgba(255, 255, 255, 0.9);
}

/* Responsive carousel controls based on space level */
.testimonial-feature-container.minimal ::v-deep .carousel-indicators {
  display: none;
}

.testimonial-feature-container.minimal ::v-deep .carousel-control-prev,
.testimonial-feature-container.minimal ::v-deep .carousel-control-next {
  width: 30px;
  opacity: 0.7;
}

.testimonial-feature-container.limited ::v-deep .carousel-control-prev,
.testimonial-feature-container.limited ::v-deep .carousel-control-next {
  width: 35px;
}

.testimonial-feature-container.normal ::v-deep .carousel-control-prev,
.testimonial-feature-container.normal ::v-deep .carousel-control-next {
  width: 40px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .testimonial-slide-container {
    padding: 15px;
  }
  
  .patient-avatar {
    width: 45px;
    height: 45px;
    margin-right: 10px;
  }
  
  .patient-name {
    font-size: 15px;
  }
  
  .testimonial-quote {
    font-size: 13px;
  }
  
  .star {
    font-size: 13px;
  }
  
  .testimonial-feature-container ::v-deep .carousel-control-prev,
  .testimonial-feature-container ::v-deep .carousel-control-next {
    width: 30px;
  }
}

/* Background decoration and animations */
.background-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.quote-mark {
  position: absolute;
  font-size: 120px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.05);
  font-family: serif;
  line-height: 1;
  animation: floatAndFade 8s ease-in-out infinite;
}

.quote-mark-1 {
  top: 10px;
  left: 10px;
  animation-delay: 0s;
}

.quote-mark-2 {
  bottom: 10px;
  right: 10px;
  animation-delay: 4s;
}

.trust-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 32px;
  height: 32px;
  background: rgba(255, 215, 0, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #ffd700;
  animation: trustPulse 6s ease-in-out infinite;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 215, 0, 0.3);
}

/* Verified badge */
.verified-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  animation: slideInFromTop 0.8s ease-out 0.1s both;
}

.verified-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 184, 148, 0.2);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: clamp(10px, 1.3vw, 13px);
  font-weight: 600;
  color: #00b894;
  border: 1px solid rgba(0, 184, 148, 0.3);
  backdrop-filter: blur(5px);
}

.verified-badge i {
  font-size: 12px;
  animation: checkmarkPulse 3s ease-in-out infinite;
}

/* Avatar glow effect */
.avatar-glow {
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border-radius: 50%;
  background: linear-gradient(45deg, #ffd700, #fff, #ffd700);
  opacity: 0;
  animation: avatarGlow 4s ease-in-out infinite;
  z-index: -1;
}

/* Quote icon */
.quote-icon {
  position: absolute;
  top: -10px;
  left: -5px;
  font-size: 24px;
  color: rgba(255, 215, 0, 0.6);
  animation: quoteFloat 3s ease-in-out infinite;
}

/* Trust indicators */
.trust-indicators {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  gap: 12px;
  animation: slideInFromBottom 0.8s ease-out 0.7s both;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: clamp(10px, 1.2vw, 12px);
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.1);
  padding: 6px 10px;
  border-radius: 12px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  flex: 1;
  justify-content: center;
}

.trust-item i {
  font-size: 12px;
  color: #00b894;
}

/* Testimonial text styling */
.testimonial-text {
  margin-bottom: 20px;
  position: relative;
}

/* Glassmorphism overlay */
.glass-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 16px;
  pointer-events: none;
  z-index: 1;
}

/* Keyframe animations */
@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInFromBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes starGlow {
  0%, 100% { 
    filter: brightness(1);
    transform: scale(1);
  }
  50% { 
    filter: brightness(1.2);
    transform: scale(1.05);
  }
}

@keyframes trustPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}

@keyframes checkmarkPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

@keyframes avatarGlow {
  0%, 100% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.05);
  }
}

@keyframes quoteFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes floatAndFade {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.05;
  }
  50% {
    transform: translateY(-10px) rotate(5deg);
    opacity: 0.1;
  }
}

/* Respect reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .testimonial-content,
  .star,
  .trust-badge,
  .avatar-glow,
  .quote-mark {
    animation: none;
  }
  
  .testimonial-content:hover {
    transform: none;
  }
}

/* Emergency fallback for very small screens */
@media (max-height: 500px) {
  .testimonial-feature-container ::v-deep .carousel-indicators {
    display: none !important;
  }
  
  .testimonial-feature-container ::v-deep .carousel-control-prev,
  .testimonial-feature-container ::v-deep .carousel-control-next {
    width: 25px !important;
    opacity: 0.8 !important;
  }
  
  .verified-container {
    margin-bottom: 12px;
  }
  
  .patient-avatar {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }
  
  .trust-indicators {
    margin-top: 12px;
    gap: 8px;
  }
  
  .trust-item {
    padding: 4px 8px;
  }
}
</style>